import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, LifeBuoy, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MaskedLine } from "@/components/landing/anim";
import { LogoMark } from "@/components/brand/Logo";

const CONCERT_IMG =
  "https://images.unsplash.com/photo-1765224747205-3c9c23f0553c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxsaXZlJTIwY29uY2VydCUyMGNyb3dkJTIwZGFyayUyMGJsdWV8ZW58MHx8fHwxNzg1NDcyMjUyfDA&ixlib=rb-4.1.0&q=85";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500 transition-colors";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const [params] = useSearchParams();
  const authError = params.get("error") === "auth";
  const paramTier = params.get("tier") || "";
  const storedTier = typeof window !== "undefined" ? (window.localStorage.getItem("noizbloc_pending_tier") || "") : "";
  const preselectedTier = ["artist", "pro", "label"].includes(paramTier)
    ? paramTier
    : (["artist", "pro", "label"].includes(storedTier) ? storedTier : "");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState({ open: false, email: "", busy: false, sent: false });

  useEffect(() => {
    if (paramTier) {
      try { window.localStorage.setItem("noizbloc_pending_tier", paramTier); } catch (e) { /* ignore */ }
    }
  }, [paramTier]);

  useEffect(() => {
    if (!loading && user) {
      const t = preselectedTier ? `?tier=${preselectedTier}` : "";
      navigate(`/dashboard${t}`, { replace: true });
    }
  }, [user, loading, navigate, preselectedTier]);

  const signInGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // (The `redirectUrl` below is derived dynamically. `https://auth.emergentagent.com` is the
    // fixed Emergent-managed auth service URL — the playbook uses it verbatim.)
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return toast.error("Enter your email");
    if (!form.password) return toast.error("Enter your password");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      setUser(data);
      toast.success("Welcome back");
      const t = preselectedTier ? `?tier=${preselectedTier}` : "";
      navigate(`/dashboard${t}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  const openForgot = () => setForgot({ open: true, email: form.email, busy: false, sent: false });
  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgot.email.trim() || !forgot.email.includes("@")) {
      return toast.error("Enter a valid email");
    }
    setForgot((f) => ({ ...f, busy: true }));
    try {
      await api.post("/auth/forgot-password", { email: forgot.email.trim().toLowerCase() });
      setForgot((f) => ({ ...f, busy: false, sent: true }));
    } catch (err) {
      setForgot((f) => ({ ...f, busy: false }));
      toast.error(formatApiError(err, "Couldn't send the reset link"));
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#020617] text-white" data-testid="login-page">
      {/* Left: atmospheric imagery */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={CONCERT_IMG} alt="Live concert" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-[#020617]/10" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <LogoMark className="text-white" />
            <span className="font-display text-xl font-semibold">Noizbloc</span>
          </div>
          <div>
            <h2 className="font-display text-4xl xl:text-5xl font-medium tracking-tight leading-[1.05]">
              <MaskedLine delay={0.1}>Get your</MaskedLine>
              <MaskedLine delay={0.2}>song <span className="text-blue-500">heard.</span></MaskedLine>
            </h2>
            <p className="mt-5 text-slate-400 max-w-sm">
              Sign in to your artist or label workspace and pick up where you left off.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <button
          data-testid="login-back-btn"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={16} /> Back to home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-sm w-full"
        >
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-slate-400">Welcome back</span>
          <h1 className="mt-4 font-display text-4xl font-medium tracking-tight">Sign in</h1>
          <p className="mt-3 text-slate-400">
            Use the email you signed up with, or continue with Google.
          </p>

          {authError && (
            <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">
              Sign-in failed. Please try again.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-3.5" data-testid="login-form">
            <input
              data-testid="login-email"
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              autoComplete="email"
            />
            <input
              data-testid="login-password"
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
              autoComplete="current-password"
            />
            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-6 py-4 hover:bg-slate-200 active:scale-[0.99] transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
            </button>
            <div className="pt-1 flex justify-end">
              <button
                type="button"
                data-testid="forgot-password-btn"
                onClick={openForgot}
                className="text-xs text-slate-400 hover:text-blue-300 transition-colors"
              >
                Forgot password?
              </button>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
            <div className="h-px flex-1 bg-white/10" />
            <span>or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={signInGoogle}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white/5 border border-white/10 hover:bg-white/[0.08] text-white font-medium px-6 py-3.5 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <p className="mt-8 text-xs text-slate-500 leading-relaxed">
            New to Noizbloc?{" "}
            <button
              data-testid="login-register-link"
              onClick={() => navigate("/register")}
              className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
            >
              Create an account
            </button>
          </p>

          <div
            data-testid="support-footer"
            className="mt-8 pt-6 border-t border-white/5 flex items-center gap-2.5 text-xs text-slate-500"
          >
            <LifeBuoy size={13} className="text-slate-500" />
            <span>Facing issues signing in?</span>
            <a
              data-testid="support-email-link"
              href="mailto:support@noizbloc.com"
              className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 underline underline-offset-2"
            >
              <Mail size={12} /> support@noizbloc.com
            </a>
          </div>
        </motion.div>
      </div>

      <Dialog open={forgot.open} onOpenChange={(v) => setForgot((f) => ({ ...f, open: v, sent: v ? f.sent : false }))}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reset your password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter the email you signed up with — we&apos;ll send you a link to choose a new password.
            </DialogDescription>
          </DialogHeader>
          {forgot.sent ? (
            <div data-testid="forgot-sent" className="mt-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4">
              <div className="text-sm text-emerald-200/90">
                Check your inbox. If <span className="text-white">{forgot.email}</span> is registered with Noizbloc,
                a reset link is on its way. The link expires in 60 minutes.
              </div>
              <button
                onClick={() => setForgot({ open: false, email: "", busy: false, sent: false })}
                className="mt-4 rounded-full bg-white text-black text-sm font-medium px-5 py-2.5 hover:bg-slate-200"
              >
                Got it
              </button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-3 mt-2" data-testid="forgot-form">
              <input
                data-testid="forgot-email-input"
                type="email"
                value={forgot.email}
                onChange={(e) => setForgot((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email address"
                className={inputCls}
                autoFocus
              />
              <DialogFooter>
                <button
                  type="button"
                  onClick={() => setForgot({ open: false, email: "", busy: false, sent: false })}
                  className="rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={forgot.busy}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium text-sm px-6 py-2.5 hover:bg-slate-200 disabled:opacity-60"
                >
                  {forgot.busy ? <Loader2 size={16} className="animate-spin" /> : "Send reset link"}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
